'use client';
import Button from '@ui/Button';
import { useNotifications } from '@util/hooks/useNotifcations';
import { useAuth } from 'context/AuthContext';
import Link from 'next/link';
import {
  BoxIcon,
  CartIcon,
  HeartIcon,
  ListOutlineIcon,
  StorefrontIcon,
  PersonOutlineIcon,
  GarageIcon,
  RotateIcon,
  FollowingIcons,
  LocationIcon,
  EmptyWalletIcon,
  ShootingStarIcon,
  BalancesIcon,
  StarOutlineIcon,
  PrintALabelIcon,
  SettingsIcon,
  PlaneIcon,
  CurationsIcon,
} from '../icons';
import { GavelIconOutline } from '@c/banners/AuctionBanner';
import Boxes from '@c/icons/Boxes';
import { staticCategories } from '../navigation/utils/categories';
import { useCallback, useMemo, useState } from 'react';
import MenuHomeIcon from './assets/icons/MenuHomeIcon';
import MenuSquaresIcon from './assets/icons/MenuSquaresIcon';
import MenuMessagesIcon from './assets/icons/MenuMessagesIcon';
import MenuHandIcon from './assets/icons/MenuHandIcon';
import MenuBalanceIcon from './assets/icons/MenuBalanceIcon';
import { AppStoreButtons } from 'app/get-the-app/components/GetTheApp';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileItem from '@c/ProfileItem';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { FaBicycle, FaMoneyBillWave } from 'react-icons/fa';
import { IoPricetagOutline } from 'react-icons/io5';

// Menu data structure supporting multiple levels
interface MenuLink {
  label: string;
  href: string;
  icon?: React.ReactNode;
  hasNotifications?: boolean;
  notificationCount?: number;
  hasSubmenus?: boolean;
}

interface MenuSection {
  name: string;
  items: MenuLink[];
}

interface Menu {
  id: string;
  title: string;
  icon?: React.ReactNode;
  hasSubmenus?: boolean;
  hasNotifications?: boolean;
  href?: string;
  sections?: MenuSection[];
  items?: MenuLink[];
}

// Animation variants for modal-like transitions
const menuVariants = {
  initial: {
    x: '100%',
    opacity: 0,
  },
  animate: {
    x: '0%',
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 200,
      duration: 0.5,
    },
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 200,
      duration: 0.4,
    },
  },
};

const MenuHeader = ({
  title,
  onBack,
  onClose,
}: {
  title: string;
  onBack: () => void;
  onClose: () => void;
}) => (
  <div className="sticky top-0 z-10 bg-brand-white">
    <div className="flex w-full items-center justify-between px-4 py-6">
      <button
        type="button"
        className="flex cursor-pointer items-center gap-x-2"
        onClick={onBack}
      >
        <ChevronLeftIcon className="h-[2.4rem] w-[2.4rem] text-brand-secondary" />
        <h2 className="text-[1.8rem] font-medium text-brand-secondary">
          {title}
        </h2>
      </button>
      <button
        type="button"
        onClick={onClose}
        className="text-black transition-colors hover:text-gray-600"
      >
        <svg
          className="h-[2.4rem] w-[2.4rem]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </div>
);

const MenuItem = ({
  icon,
  text,
  href,
  hasSubmenus = false,
  hasNotifications = false,
  notificationCount = 0,
  onClick,
}: {
  icon?: React.ReactNode;
  text: string;
  href?: string;
  hasSubmenus?: boolean;
  hasNotifications?: boolean;
  notificationCount?: number;
  onClick?: () => void;
}) => {
  const Content = () => (
    <div className="flex w-full items-center justify-between px-4 py-6">
      <div className="flex items-center gap-x-4">
        {icon && icon}
        <p className="text-[1.8rem] font-medium text-black">{text}</p>

        {hasNotifications && (
          <div className="flex items-center gap-x-2">
            {notificationCount > 0 && (
              <span className="rounded-full bg-brand-secondary px-4 py-2 text-[1rem] font-medium text-white">
                {notificationCount}
              </span>
            )}
          </div>
        )}
      </div>

      {hasSubmenus && (
        <ChevronRightIcon className="h-[2rem] w-[2rem] text-gray-400" />
      )}
    </div>
  );

  if (href && !hasSubmenus) {
    return (
      <Link
        href={href}
        className="flex w-full items-center justify-between"
        onClick={onClick}
      >
        <Content />
      </Link>
    );
  }

  return (
    <button
      type="button"
      className="flex w-full items-center justify-between"
      onClick={onClick}
    >
      <Content />
    </button>
  );
};

const MenuSection = ({
  sectionName,
  children,
  onClick,
  isPrimary = false,
}: {
  sectionName: string;
  children?: React.ReactNode;
  onClick?: () => void;
  isPrimary?: boolean;
}) => (
  <div className="flex flex-col">
    <button
      type="button"
      className="flex items-center justify-between px-4 py-6 transition-colors hover:bg-gray-50"
      onClick={onClick}
    >
      <p
        className={`capitalize ${
          isPrimary
            ? 'text-[1.8rem] font-medium text-black'
            : 'text-[1.6rem] font-semibold text-zinc-700'
        }`}
      >
        {sectionName}
      </p>
      <ChevronRightIcon className="h-[2rem] w-[2rem] text-gray-500" />
    </button>
    {children}
  </div>
);

const MobileSidebar = ({ dismiss }: { dismiss: () => void }) => {
  const { logout, userDoc, user } = useAuth();

  const [navigationStack, setNavigationStack] = useState<string[]>(['main']);

  const {
    unreadCount,
    pendingOrdersCount,
    pendingOffersCount,
    pendingReviewsCount,
    requiresStripeDetails,
    integrationRequestsCount,
  } = useNotifications();

  // Define all menus with their structure
  // Using useMemo to avoid re-rendering
  const menus: Record<string, Menu> = useMemo(() => {
    const sections = [];

    // Add ADMIN CONTROLS section for admin/support users
    if (
      userDoc?.roles?.admin ||
      userDoc?.roles?.support ||
      userDoc?.roles?.curated_lists ||
      userDoc?.roles?.marketing ||
      userDoc?.roles?.seo
    ) {
      const adminItems = [];

      if (userDoc?.roles?.admin) {
        adminItems.push({
          label: 'Admin',
          href: '/admin',
          icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
        });
      }

      if (userDoc?.roles?.support) {
        adminItems.push({
          label: 'Support',
          href: '/support',
          icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
        });
      }

      if (userDoc?.roles?.curated_lists || userDoc?.roles?.admin) {
        adminItems.push({
          label: 'Curated Lists',
          href: '/dashboard/curated-lists',
          icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
        });
      }

      if (userDoc?.roles?.marketing || userDoc?.roles?.admin) {
        adminItems.push({
          label: 'Banners',
          href: '/dashboard/banners',
          icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
        });
      }

      if (userDoc?.roles?.seo || userDoc?.roles?.admin) {
        adminItems.push(
          {
            label: 'Categories',
            href: '/dashboard/categories',
            icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
          },
          {
            label: 'Brands',
            href: '/dashboard/brands',
            icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
          }
        );
      }

      if (adminItems.length > 0) {
        sections.push({
          name: 'ADMIN CONTROLS',
          items: adminItems,
        });
      }
    }

    // Add ACCOUNT section for general account features
    if (userDoc && !user?.isAnonymous) {
      const accountItems: MenuLink[] = [
        {
          label: 'Profile',
          href: '/dashboard/my-profile',
          icon: <PersonOutlineIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'My Garage',
          href: '/dashboard/my-garage',
          icon: <GarageIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Addresses',
          href: '/dashboard/addresses',
          icon: <LocationIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Payments',
          href: '/dashboard/payments',
          icon: <EmptyWalletIcon className="h-[3rem] w-[3rem] text-black" />,
        },
      ];

      sections.push({
        name: 'ACCOUNT',
        items: accountItems,
      });
    }

    // Only include SELLING section if user is a seller (account_activated)
    if (userDoc?.account_activated) {
      const sellingItems = [
        {
          label: 'Seller Dashboard',
          href: '/dashboard/shop-overview',
          icon: <StorefrontIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Listings',
          href: '/dashboard/listings',
          icon: <ListOutlineIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Orders',
          href: '/dashboard/orders',
          icon: <BoxIcon className="h-[3rem] w-[3rem] text-black" />,
          hasNotifications: !!pendingOrdersCount && pendingOrdersCount > 0,
          notificationCount: pendingOrdersCount,
        },
        {
          label: 'Coupons',
          href: '/dashboard/coupons',
          icon: <IoPricetagOutline className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Balance',
          href: '/dashboard/balance',
          icon: <BalancesIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Seller Reviews',
          href: '/dashboard/ratings',
          icon: <StarOutlineIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Print a Label',
          href: '/dashboard/shipping-center',
          icon: <PrintALabelIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Seller Settings',
          href: '/dashboard/shop-settings',
          icon: <SettingsIcon className="h-[3rem] w-[3rem] text-black" />,
          hasNotifications: !!requiresStripeDetails,
          notificationCount: requiresStripeDetails ? 1 : 0,
        },
        {
          label: 'Vacation Mode',
          href: '/dashboard/vacation-mode',
          icon: <PlaneIcon className="h-[3rem] w-[3rem] text-black" />,
        },
        {
          label: 'Integrations',
          href: '/dashboard/integrations',
          icon: <RotateIcon className="h-[3rem] w-[3rem] text-black" />,
          hasNotifications:
            userDoc?.roles?.admin &&
            !!integrationRequestsCount &&
            integrationRequestsCount > 0,
          notificationCount: userDoc?.roles?.admin
            ? integrationRequestsCount
            : 0,
        },
      ];

      if (
        userDoc.uid === 'qlcEanuNfBZOpI7CmwgLN0QKPlo1' ||
        userDoc.roles?.admin
      ) {
        sellingItems.push({
          label: 'Seller Returns',
          href: '/dashboard/seller-returns',
          icon: <RotateIcon className="h-[3rem] w-[3rem] text-black" />,
        });
      }

      if (userDoc.roles?.integrations) {
        sellingItems.push({
          label: 'Billing',
          href: '/dashboard/billing',
          icon: (
            <FaMoneyBillWave
              style={{ width: '3rem', height: '3rem', color: 'black' }}
            />
          ),
          hasNotifications:
            userDoc?.roles?.admin &&
            !!integrationRequestsCount &&
            integrationRequestsCount > 0,
          notificationCount: userDoc?.roles?.admin
            ? integrationRequestsCount
            : 0,
        });
      }

      if (userDoc.roles?.reseller || userDoc.roles?.support) {
        sellingItems.push({
          label: 'Purchase Entry',
          href: '/dashboard/purchase-entry',
          icon: <Boxes />,
        });
      }

      sections.push({
        name: 'SELLING',
        items: sellingItems,
      });
    }

    // Always include BUYING section
    const buyingItems = [
      {
        label: 'Purchases',
        href: userDoc ? '/dashboard/purchases' : '/login',
        icon: <CartIcon className="h-[3rem] w-[3rem] text-black" />,
      },
      {
        label: 'Returns',
        href: '/dashboard/returns',
        icon: <RotateIcon className="h-[3rem] w-[3rem] text-black" />,
      },
      {
        label: 'Favorites',
        href: userDoc ? '/dashboard/favorites' : '/login',
        icon: <HeartIcon className="h-[3rem] w-[3rem] text-black" />,
      },
      {
        label: 'Following',
        href: '/dashboard/following',
        icon: <FollowingIcons className="h-[3rem] w-[3rem] text-black" />,
      },
      {
        label: 'Bids',
        href: '/dashboard/bids',
        icon: <GavelIconOutline />,
      },
      {
        label: 'Reviews',
        href: '/dashboard/reviews',
        icon: <ShootingStarIcon className="h-[3rem] w-[3rem] text-black" />,
        hasNotifications: !!pendingReviewsCount && pendingReviewsCount > 0,
        notificationCount: pendingReviewsCount,
      },
    ];

    if (userDoc?.roles?.bike_shipper || userDoc?.roles?.admin) {
      buyingItems.push({
        label: 'Ship my Dirt Bike',
        href: '/dashboard/bike-shipments',
        icon: (
          <FaBicycle
            style={{ width: '3rem', height: '3rem', color: 'black' }}
          />
        ),
      });
    }

    sections.push({
      name: 'BUYING',
      items: buyingItems,
    });

    return {
      main: {
        id: 'main',
        title: 'Menu',
        items: [
          {
            label: 'Home',
            href: '/',
            icon: <MenuHomeIcon className="h-[3rem] w-[3rem] text-black" />,
          },
          {
            label: 'Browse categories',
            href: '#',
            icon: <MenuSquaresIcon className="h-[3rem] w-[3rem] text-black" />,
            hasSubmenus: true,
          },
          {
            label: 'Messages',
            href: userDoc ? '/dashboard/messages' : '/login',
            icon: <MenuMessagesIcon className="h-[3rem] w-[3rem] text-black" />,
            hasNotifications: !!unreadCount && unreadCount > 0,
            notificationCount: unreadCount,
          },
        ],
        sections,
      },
      categories: {
        id: 'categories',
        title: 'Categories',
        items: staticCategories
          .filter((cat) => !cat.excludeFromNav)
          .map((category) => ({
            label: category.label,
            href: category.href,
            hasSubmenus: true,
          })),
      },
      ...staticCategories
        .filter((cat) => !cat.excludeFromNav)
        .reduce((acc, category) => {
          const categoryId = category.label.toLowerCase().replace(/\s+/g, '-');

          acc[categoryId] = {
            id: categoryId,
            title: category.label,
            sections:
              category.sections?.map((section) => ({
                name: section.label,
                items:
                  section.links?.map((link) => ({
                    label: link.label,
                    href: link.href,
                  })) || [],
              })) || [],
          };

          category.sections?.forEach((section) => {
            if (section.links && section.links.length > 0) {
              const sectionId = `${categoryId}-${section.label
                .toLowerCase()
                .replace(/\s+/g, '-')}`;

              acc[sectionId] = {
                id: sectionId,
                title: section.label,
                items: section.links.map((link) => ({
                  label: link.label,
                  href: link.href,
                })),
              };
            }
          });

          return acc;
        }, {} as Record<string, Menu>),
      selling: {
        id: 'selling',
        title: 'Selling',
        items: [
          {
            label: 'Listings',
            href: '/dashboard/listings',
            icon: <ListOutlineIcon className="h-[3rem] w-[3rem] text-black" />,
          },
          {
            label: 'Orders',
            href: '/dashboard/orders',
            icon: <BoxIcon className="h-[3rem] w-[3rem] text-black" />,
            hasNotifications: !!pendingOrdersCount && pendingOrdersCount > 0,
            notificationCount: pendingOrdersCount,
          },
          {
            label: 'Balance',
            href: '/dashboard/balance',
            icon: <MenuBalanceIcon className="h-[3rem] w-[3rem] text-black" />,
          },
          {
            label: 'Seller Dashboard',
            href: '/dashboard/shop-overview',
            icon: <StorefrontIcon className="h-[3rem] w-[3rem] text-black" />,
          },
        ],
      },
      buying: {
        id: 'buying',
        title: 'Buying',
        items: [
          {
            label: 'Purchases',
            href: '/dashboard/purchases',
            icon: <CartIcon className="h-[3rem] w-[3rem] text-black" />,
          },
          {
            label: 'Favorites',
            href: '/dashboard/favorites',
            icon: <HeartIcon className="h-[3rem] w-[3rem] text-black" />,
          },
        ],
      },
    };
  }, [unreadCount, pendingOrdersCount, pendingOffersCount, userDoc]);

  const currentMenuId = navigationStack[navigationStack.length - 1];
  const currentMenu = menus[currentMenuId];
  const previousMenu = menus[navigationStack[navigationStack.length - 2]];

  const navigateToMenu = useCallback((menuId: string) => {
    setNavigationStack((prev) => [...prev, menuId]);
  }, []);

  const navigateBack = useCallback(() => {
    setNavigationStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const handleMenuItemClick = useCallback(
    (item: MenuLink) => {
      if (item.label === 'Browse categories') {
        navigateToMenu('categories');
      } else if (item.hasSubmenus) {
        const menuId = item.label.toLowerCase().replace(/\s+/g, '-');
        if (menus[menuId]) {
          navigateToMenu(menuId);
        }
      } else if (item.href && item.href !== '#') {
        dismiss();
      }
    },
    [navigateToMenu, dismiss, menus]
  );

  const handleSectionClick = useCallback(
    (sectionName: string) => {
      const currentCategoryId = currentMenuId;
      const sectionId = `${currentCategoryId}-${sectionName
        .toLowerCase()
        .replace(/\s+/g, '-')}`;
      if (menus[sectionId]) {
        navigateToMenu(sectionId);
      }
    },
    [navigateToMenu, menus, currentMenuId]
  );

  const renderMenu = (menu: Menu) => (
    <div className="flex flex-col px-4">
      {/* Direct items without sections */}
      {menu.items?.map((item, index) => (
        <MenuItem
          key={`${item.label}-${index}`}
          icon={item.icon}
          text={item.label}
          href={item.href}
          hasSubmenus={item.hasSubmenus}
          hasNotifications={item.hasNotifications}
          notificationCount={item.notificationCount}
          onClick={() => handleMenuItemClick(item)}
        />
      ))}

      {/* Sectioned items */}
      {menu.sections?.map((section, sectionIndex) => (
        <MenuSection
          key={`${section.name}-${sectionIndex}`}
          sectionName={section.name}
          onClick={() => handleSectionClick(section.name)}
          isPrimary={menu.id !== 'main'}
        >
          {menu.id === 'main' &&
            section.items.map((item, itemIndex) => (
              <MenuItem
                key={`${item.label}-${itemIndex}`}
                icon={item.icon}
                text={item.label}
                href={item.href}
                hasSubmenus={item.hasSubmenus}
                hasNotifications={item.hasNotifications}
                notificationCount={item.notificationCount}
                onClick={() => handleMenuItemClick(item)}
              />
            ))}
        </MenuSection>
      ))}
    </div>
  );

  return (
    <div className="relative h-full min-w-[26rem] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMenuId}
          variants={menuVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="absolute inset-0 flex flex-col bg-brand-white"
        >
          {/* Show header for submenus */}
          {currentMenuId !== 'main' && (
            <MenuHeader
              title={previousMenu?.title ?? ''}
              onBack={navigateBack}
              onClose={dismiss}
            />
          )}

          {currentMenuId !== 'main' && (
            <h2 className="px-8 py-4 text-[2.5rem] font-bold text-black">
              {currentMenu.title}
            </h2>
          )}

          {currentMenuId === 'main' && (
            <>
              <div className="sticky top-0 z-10 bg-brand-white">
                <div className="flex w-full items-center justify-end px-4 py-6">
                  <button
                    onClick={dismiss}
                    className="text-black transition-colors hover:text-gray-600"
                  >
                    <svg
                      className="h-[2.4rem] w-[2.4rem]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-y-8 p-4">
                {user && !user.isAnonymous && userDoc && (
                  <>
                    <ProfileItem dismiss={() => dismiss()} />

                    <Button
                      text={
                        userDoc.account_activated
                          ? 'Sell an item'
                          : 'Become a seller'
                      }
                      width="fluid"
                      type="secondary"
                      className="!font-medium"
                      onClick={() => dismiss()}
                      href={
                        userDoc.account_activated
                          ? '/create-listing'
                          : '/become-a-seller'
                      }
                    />
                  </>
                )}
              </div>
            </>
          )}

          <div className="flex-1 overflow-y-auto pb-[1rem]">
            {renderMenu(currentMenu)}

            {/* App Store section for main menu */}
            {currentMenuId === 'main' && (
              <>
                <div className="mt-8 flex flex-col gap-y-2 border-b-[1.5px] border-[#D9D9D9] px-8">
                  <p className="text-[1.8rem] font-bold text-black">
                    Download the MX Locker App
                  </p>
                  <AppStoreButtons />
                </div>

                <div className="mt-4 flex w-full flex-col gap-y-12 px-8 pb-16">
                  {userDoc ? (
                    <button
                      type="button"
                      className="h-[5.5rem] w-full rounded-[2rem] border border-black bg-transparent px-4 py-2 text-[1.8rem] font-medium text-black"
                      onClick={() => {
                        logout();
                      }}
                    >
                      Logout
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="h-[5.5rem] w-full rounded-[2rem] bg-brand-secondary px-4 py-2 text-[1.8rem] font-medium text-white"
                      >
                        Sign Up
                      </button>

                      <p className={'text-[1.8rem] font-medium text-black'}>
                        Already have an account?{' '}
                        <span>
                          <Link
                            href={'/login'}
                            className="font-semibold text-brand-secondary"
                          >
                            Log In
                          </Link>
                        </span>
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MobileSidebar;
